
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { authSchema } from "./validationSchema";
import { checkIfUserIsAdmin } from "@/services/user";
import { z } from "zod";

export const signUp = async (formData: {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}): Promise<void> => {
  try {
    // Validate form data
    const validatedData = authSchema.parse(formData);
    
    // Sign up the user
    const { data, error } = await supabase.auth.signUp({
      email: validatedData.email,
      password: validatedData.password,
      options: {
        data: {
          first_name: validatedData.first_name,
          last_name: validatedData.last_name,
        },
      },
    });
    
    if (error) {
      throw error;
    }
    
    toast({
      title: "Account created successfully!",
      description: "Please check your email to verify your account.",
    });
    
  } catch (error: any) {
    console.error("Sign up error:", error);
    if (error instanceof z.ZodError) {
      throw new Error(error.errors[0].message);
    } else {
      throw new Error(error.message || "Failed to create account");
    }
  }
};

export const signIn = async (
  email: string, 
  password: string
): Promise<{user: any, isAdmin: boolean, role?: string}> => {
  try {
    // Use the updated signInWithPassword method instead of signIn
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      throw error;
    }
    
    if (!data.user) {
      throw new Error("No user returned after sign in");
    }
    
    // Check if the user is an admin
    const { id } = data.user;
    const isAdmin = await checkIfUserIsAdmin(id);
    
    // Get the user's role from the profiles table
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', id)
      .single();
      
    const role = profileData?.role || 'learner';
    
    if (profileError) {
      console.warn("Error fetching user profile:", profileError);
    }
    
    toast({
      title: "Welcome back!",
      description: "You have successfully signed in.",
    });
    
    return { user: data.user, isAdmin, role };
    
  } catch (error: any) {
    console.error("Sign in error:", error);
    throw new Error(error.message || "Failed to sign in");
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  if (!email) {
    throw new Error("Please enter your email address to reset your password");
  }

  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/auth?type=recovery",
    });

    if (error) {
      throw error;
    }

    toast({
      title: "Password reset email sent",
      description: "Check your inbox for instructions to reset your password",
    });
  } catch (error: any) {
    console.error("Password reset error:", error);
    throw new Error(error.message || "Failed to send reset email");
  }
};
